import { useQuery } from "@tanstack/react-query";
import { getActiveSessions } from "@/api/auth";
import { useAuthStore } from "@/stores/auth";
import { decodeJwt } from "@/utils/token";
import { QUERY_KEYS } from "@/lib/query-constants";

interface SessionRecord {
  sessionIdentifier?: string;
  loggedInAt?: string;
  deviceType?: string;
  browserType?: string;
  currentSession?: boolean;
  [key: string]: unknown;
}

function getCurrentSessionId(): string | null {
  const token = useAuthStore.getState().token;
  if (!token) return null;
  return decodeJwt(token)?.issuer?.sessionId ?? null;
}

function toSessionRecords(data: unknown): SessionRecord[] {
  if (Array.isArray(data)) return data as SessionRecord[];
  if (data && typeof data === "object") {
    const nested = (data as { sessions?: unknown }).sessions;
    if (Array.isArray(nested)) return nested as SessionRecord[];
  }
  return [];
}

export function useAccountSessions() {
  return useQuery({
    queryKey: QUERY_KEYS.account.sessions,
    queryFn: async (): Promise<SessionRecord[]> => {
      const currentSessionId = getCurrentSessionId();
      const res = await getActiveSessions();
      return toSessionRecords(res).map((s) => ({
        ...s,
        currentSession: s.sessionIdentifier === currentSessionId,
      }));
    },
    staleTime: 30_000,
  });
}
